// Cloudflare R2 video client (Phase 4/5).
//
// Talks to the backend presign endpoints and uploads/plays bytes directly
// against R2 using short-lived presigned URLs. The buckets stay private —
// auth is enforced server-side by verifying the Firebase ID token before any
// URL is signed.
//
// This module is ADDITIVE and only used when the video backend flag is "r2"
// (see src/config/videoBackend.js). It does not touch the live Firebase
// upload path.
//
// Backend contract (Firebase Functions, Python HTTP):
//   POST /v1/video/upload-url
//     body: { certId, contentType, fileSize, source }
//     resp (small):     { mode: "single", key, url }
//     resp (multipart): { mode: "multipart", key, uploadId, partSize, partUrls: [url, ...] }
//   POST /v1/video/complete-upload
//     body: { key, uploadId, parts: [{ partNumber, etag }, ...] }
//   POST /v1/video/abort-upload
//     body: { key, uploadId }
//   POST /v1/video/playback-url
//     body: { key }   resp: { url }
// All requests send Authorization: Bearer <Firebase ID token>.

import { auth } from "../firebase";
import { resolveApiBaseUrl } from "./apiBase";
import { isR2Backend } from "../config/videoBackend";

// Files at/above this size use multipart upload (S3 minimum part size is 5 MB;
// we use larger parts to keep the part count and presign payload reasonable
// for ~1.8 GB GoPro clips).
const MULTIPART_THRESHOLD = 100 * 1024 * 1024; // 100 MB

// Transient failures (cellular blips, the Wi-Fi→cellular handoff after leaving
// the GoPro AP, brief R2/5xx hiccups) are common on large mobile uploads. Retry
// each network operation a few times with exponential backoff before failing.
const MAX_RETRIES = 4;
const RETRY_BASE_MS = 1200;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function getIdToken() {
  const user = auth?.currentUser;
  if (!user) throw new Error("Not signed in — cannot request video URL");
  return user.getIdToken();
}

async function postJson(path, body) {
  const baseUrl = resolveApiBaseUrl();
  let lastErr;
  for (let attempt = 0; attempt <= MAX_RETRIES; attempt += 1) {
    try {
      const token = await getIdToken();
      const res = await fetch(`${baseUrl}${path}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body || {}),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        // 5xx / 429 are transient; 4xx (auth, bad request) are not.
        if ((res.status >= 500 || res.status === 429) && attempt < MAX_RETRIES) {
          lastErr = new Error(text || `Request failed (${res.status})`);
          await sleep(RETRY_BASE_MS * 2 ** attempt);
          continue;
        }
        throw new Error(text || `Request failed (${res.status})`);
      }
      return res.json();
    } catch (err) {
      // A failed fetch (no response) throws a TypeError — retryable.
      const isNetwork = err instanceof TypeError || /network|fetch|load failed/i.test(err?.message || "");
      if (isNetwork && attempt < MAX_RETRIES) {
        lastErr = err;
        await sleep(RETRY_BASE_MS * 2 ** attempt);
        continue;
      }
      throw err;
    }
  }
  throw lastErr || new Error("Request failed");
}

// PUT a blob to a presigned URL with progress, retrying transient failures.
// Resolves with the response ETag (needed for multipart completion).
function putWithProgress(url, body, contentType, onProgress) {
  const attempt = (tryNum) =>
    new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url, true);
      if (contentType) xhr.setRequestHeader("Content-Type", contentType);
      xhr.upload.onprogress = (e) => {
        if (onProgress && e.lengthComputable) onProgress(e.loaded, e.total);
      };
      const retryOrReject = (err, retryable) => {
        if (retryable && tryNum < MAX_RETRIES) {
          setTimeout(() => attempt(tryNum + 1).then(resolve, reject), RETRY_BASE_MS * 2 ** tryNum);
        } else {
          reject(err);
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader("ETag") || "";
          resolve(etag.replace(/"/g, ""));
        } else {
          // 5xx / 408 / 429 are transient; a 403 (e.g. expired URL) is not.
          const retryable = xhr.status >= 500 || xhr.status === 408 || xhr.status === 429;
          retryOrReject(new Error(`Upload failed (${xhr.status})`), retryable);
        }
      };
      xhr.onerror = () => retryOrReject(new Error("Network error during upload"), true);
      xhr.ontimeout = () => retryOrReject(new Error("Upload timed out"), true);
      xhr.onabort = () => reject(new Error("Upload aborted"));
      xhr.send(body);
    });
  return attempt(0);
}

async function uploadSingle({ certId, blob, contentType, source, onProgress }) {
  const { key, url } = await postJson("/v1/video/upload-url", {
    certId,
    contentType,
    fileSize: blob.size,
    source,
  });
  await putWithProgress(url, blob, contentType, onProgress);
  return { key };
}

async function uploadMultipart({ certId, blob, contentType, source, onProgress }) {
  const { key, uploadId, partSize, partUrls } = await postJson("/v1/video/upload-url", {
    certId,
    contentType,
    fileSize: blob.size,
    source,
  });

  const parts = [];
  let uploadedBytes = 0;
  const total = blob.size;
  try {
    for (let i = 0; i < partUrls.length; i += 1) {
      const start = i * partSize;
      const end = Math.min(start + partSize, total);
      const chunk = blob.slice(start, end);
      const partBase = uploadedBytes;
      // eslint-disable-next-line no-await-in-loop
      const etag = await putWithProgress(partUrls[i], chunk, null, (loaded) => {
        if (onProgress) onProgress(partBase + loaded, total);
      });
      parts.push({ partNumber: i + 1, etag });
      uploadedBytes = end;
    }
    await postJson("/v1/video/complete-upload", { key, uploadId, parts });
    return { key };
  } catch (err) {
    // Best-effort cleanup so we don't leave orphaned multipart parts billing.
    await postJson("/v1/video/abort-upload", { key, uploadId }).catch(() => {});
    throw err;
  }
}

/**
 * Upload a video blob to R2. Chooses single vs multipart by size.
 * Returns { key } — store this as storagePath in Firestore.
 *   onProgress(loadedBytes, totalBytes)
 */
export async function uploadVideo({ certId, blob, contentType, source, onProgress }) {
  if (!certId || !blob) throw new Error("uploadVideo requires certId and blob");
  const type = contentType || blob.type || "application/octet-stream";
  if (blob.size >= MULTIPART_THRESHOLD) {
    return uploadMultipart({ certId, blob, contentType: type, source, onProgress });
  }
  return uploadSingle({ certId, blob, contentType: type, source, onProgress });
}

/**
 * Mint a short-lived presigned GET URL for playback/sharing.
 */
export async function getPlaybackUrl(key) {
  if (!key) throw new Error("getPlaybackUrl requires a key");
  const { url } = await postJson("/v1/video/playback-url", { key });
  return url;
}

/**
 * Resolve a playable video URL from stored metadata. On the Firebase backend
 * the durable downloadUrl is returned as-is; on R2 a fresh presigned GET is
 * minted from the storagePath (the R2 key). Falls back to any stored URL on
 * failure. Never throws.
 */
export async function resolveVideoUrl({ storagePath, downloadUrl } = {}) {
  if (isR2Backend() && storagePath) {
    try {
      return await getPlaybackUrl(storagePath);
    } catch {
      return downloadUrl || "";
    }
  }
  return downloadUrl || "";
}
